import React, { useEffect, useState } from "react";
import { 
  Building2, 
  Crown, 
  CheckCircle, 
  XCircle, 
  UserCheck,
  Users,
  AlertCircle,
  Shield
} from "lucide-react";
import adminService from "../../../../services/adminService";
import "./PlatformAccountCard.scss";

export default function PlatformAccountCard({ currentAdminId }) {
  const [data, setData] = useState({ platformUserId: null, isLocked: false, admins: [] });
  const [confirming, setConfirming] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const result = await adminService.getPlatformAccount();
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSelect = async (adminId) => {
    setLoading(true);
    try {
      await adminService.selectPlatformAccount(adminId);
      setConfirming(null);
      await load();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await adminService.cancelPlatformAccount();
      await load();
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase() || "?";
  };

  const getStatusLabel = () => {
    if (data.isLocked) return "Bloqueada";
    if (data.platformUserId) return "Ativa";
    return "Inativa";
  };

  const getStatusClass = () => {
    if (data.isLocked) return "locked";
    if (data.platformUserId) return "active";
    return "inactive";
  };

  const getStatusIcon = () => {
    if (data.isLocked) return <AlertCircle size={14} />;
    if (data.platformUserId) return <CheckCircle size={14} />;
    return <XCircle size={14} />;
  };

  return (
    <div className="platform-account-card">
      {/* Header */}
      <div className="platform-account-card__header">
        <div>
          <div className="platform-account-card__title">
            <Shield size={20} />
            Conta da Plataforma
          </div>
          <p className="platform-account-card__subtitle">
            Define qual administrador recebe as taxas de serviço e impulsionamento cobradas das vagas.
          </p>
        </div>
        <span className={`platform-account-card__status platform-account-card__status--${getStatusClass()}`}>
          {getStatusIcon()}
          {getStatusLabel()}
        </span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="platform-account-card__loading">
          Carregando administradores...
        </div>
      )}

      {/* Lista de Admins */}
      {!loading && data.admins.length > 0 && (
        <div className="platform-account-card__list">
          {data.admins.map((admin) => {
            const isCurrentOwner = admin.isCurrentOwner;
            const isConfirming = confirming === admin.id;
            const isActive = admin.id === data.platformUserId;

            return (
              <div 
                className={`platform-account-card__item ${isActive ? "platform-account-card__item--active" : ""}`} 
                key={admin.id}
              >
                {/* Avatar */}
                <div className={`platform-account-card__item-avatar ${isActive ? "platform-account-card__item-avatar--active" : ""}`}>
                  {getInitials(admin.name)}
                </div>

                {/* Info */}
                <div className="platform-account-card__item-info">
                  <div className="platform-account-card__item-top">
                    <span className="platform-account-card__item-name">
                      {admin.name}
                    </span>
                    {isCurrentOwner && (
                      <span className="platform-account-card__item-crown">
                        <Crown size={16} fill="currentColor" />
                      </span>
                    )}
                    {isActive && !isCurrentOwner && (
                      <span className="platform-account-card__item-badge platform-account-card__item-badge--selected">
                        <UserCheck size={12} />
                        Selecionado
                      </span>
                    )}
                    {isConfirming && (
                      <span className="platform-account-card__item-badge platform-account-card__item-badge--pending">
                        <AlertCircle size={12} />
                        Confirmar
                      </span>
                    )}
                  </div>
                  <div className="platform-account-card__item-email">
                    {admin.email}
                  </div>
                </div>

                {/* Actions */}
                <div className="platform-account-card__item-actions">
                  {isCurrentOwner ? (
                    admin.id === currentAdminId ? (
                      <button
                        className="platform-account-card__btn platform-account-card__btn--cancel"
                        onClick={handleCancel}
                      >
                        <XCircle size={14} />
                        Cancelar
                      </button>
                    ) : (
                      <span className="platform-account-card__btn platform-account-card__btn--disabled">
                        <Crown size={14} />
                        Dono atual
                      </span>
                    )
                  ) : isConfirming ? (
                    <>
                      <button
                        className="platform-account-card__btn platform-account-card__btn--confirm"
                        disabled={loading}
                        onClick={() => handleSelect(admin.id)}
                      >
                        <CheckCircle size={14} />
                        Confirmar
                      </button>
                      <button
                        className="platform-account-card__btn platform-account-card__btn--outline"
                        onClick={() => setConfirming(null)}
                      >
                        <XCircle size={14} />
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <button
                      className="platform-account-card__btn platform-account-card__btn--select"
                      disabled={loading || data.isLocked || isActive}
                      onClick={() => setConfirming(admin.id)}
                    >
                      <Shield size={14} />
                      {isActive ? "Selecionado" : "Selecionar como dono"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && data.admins.length === 0 && (
        <div className="platform-account-card__empty">
          <Users size={40} />
          <h3>Nenhum administrador encontrado</h3>
          <p>Adicione administradores para gerenciar a conta da plataforma.</p>
        </div>
      )}
    </div>
  );
}